import React from 'react';

import { Flex, Group, Skeleton, Text } from '@mantine/core';
import type { PlotlyTypes } from 'visyn_core/plotly';
import { PlotlyComponent } from 'visyn_core/plotly';
import { categoricalColors20 } from 'visyn_core/utils';

import type { IgProductiveChainsRawData } from '../generated/generatedOpenAPI';
import { DisplayType } from '../types/experiment';
import { toPercentages } from '../utils';
import CountPercentageToggle from './CountPercentageToggle';

interface ProductiveChainsProps {
  igProductiveChainsRawData: IgProductiveChainsRawData | undefined;
}

export default function ProductiveChainSummary({ igProductiveChainsRawData }: ProductiveChainsProps) {
  const [selectedDisplayType, setSelectedDisplayType] = React.useState<DisplayType>(DisplayType.COUNT);

  const data = React.useMemo(() => {
    if (!igProductiveChainsRawData) {
      return [];
    }
    let vals: number[] = Object.values(igProductiveChainsRawData);
    if (selectedDisplayType === DisplayType.PERCENTAGE) {
      vals = toPercentages(Object.values(igProductiveChainsRawData));
    }

    return [
      {
        type: 'bar',
        x: ['Productive', 'Unproductive'],
        y: vals,
        marker: {
          color: categoricalColors20[0],
        },
      },
    ];
  }, [igProductiveChainsRawData, selectedDisplayType]);

  if (!igProductiveChainsRawData) {
    return <Skeleton height={300} />;
  }

  const yAxisLabel: string = selectedDisplayType === DisplayType.PERCENTAGE ? 'Percentage of cells' : 'Cells';

  return (
    <Flex h="100%" justify="flex-end" direction="column" data-testid="productive-chain-summary">
      <Group justify="space-between">
        <Text>Productive Chain Distribution</Text>
        <Group gap={7}>
          <CountPercentageToggle displayType={selectedDisplayType} setDisplayType={setSelectedDisplayType} />
        </Group>
      </Group>
      <PlotlyComponent
        data={data as PlotlyTypes.Data[]}
        style={{ width: '100%' }}
        layout={{
          height: 400,
          margin: {
            t: 30,
            b: 55,
          },
          yaxis: {
            title: {
              text: yAxisLabel,
              font: { size: 16 },
            },
          },
        }}
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
        }}
      />
    </Flex>
  );
}
